import React, { ReactNodeArray, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { truncateStyles } from './styles'
import messages from '@utils/messages'

interface TruncateProps {
  wordCount: number
  allowShowMore?: boolean
  children?: string | ReactNodeArray | null
  commas?: boolean
  showFullText?: boolean
}

const enterKeyCode = 13

const Truncate: React.FC<TruncateProps> = ({
  children,
  wordCount = 10,
  allowShowMore = true,
  commas = true,
  showFullText,
}) => {
  const [showMore, setShowMore] = useState(false)
  const { classes } = truncateStyles()

  const renderShowMore = () => (
    <span
      role="button"
      tabIndex={0}
      className={classes.more}
      onClick={() => setShowMore(!showMore)}
      onKeyDown={(event) => {
        if (event.keyCode === enterKeyCode) {
          setShowMore(!showMore)
        }
      }}
    >
      <span>
        ...
        {!showMore ? (
          <FormattedMessage {...messages.MORE} />
        ) : (
          <FormattedMessage {...messages.LESS} />
        )}
      </span>
    </span>
  )

  if (!children) {
    return null
  }

  if (Array.isArray(children)) {
    return (
      <span>
        {children
          .filter(
            (child, i) =>
              (i < wordCount ||
                (i + 1 > wordCount && (showMore || showFullText === true))) &&
              child
          )
          .map((child, i) => (
            <span key={i}>
              {child}
              {(i + 2 > wordCount && (!showMore || showFullText === false)) ||
              i + 1 === children.length ||
              !commas
                ? ' '
                : ', '}
            </span>
          ))}
        {children.length - 1 > wordCount && allowShowMore && renderShowMore()}
        {children.length - 1 > wordCount && !allowShowMore && '...'}
      </span>
    )
  }

  const spliced = String(children)?.split(' ').splice(0, wordCount).join(' ')

  // Removes comma if at the end of a truncated string
  const truncated =
    spliced.split('').lastIndexOf(',') === spliced.length - 1
      ? spliced.slice(0, spliced.length - 1)
      : spliced

  const hasMoreChildrenThanWords =
    String(children)?.split(' ').length > wordCount

  const shouldShowMore = allowShowMore && hasMoreChildrenThanWords

  const showElipses =
    !allowShowMore && showFullText === false && hasMoreChildrenThanWords

  return (
    <span>
      {showMore || showFullText === true ? children : truncated}
      {shouldShowMore && renderShowMore()}
      {showElipses && '...'}
    </span>
  )
}

export default Truncate
